import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 154 (conditional keys):
//   resultsCount.search
//   resultsCount.search.singlePage
//   resultsCount
import cn from 'classnames';
import { BiData, PageNavigationClickBiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import ArrowLeft from '@wix/da-ds/pkg/Icons/24x24/ChevronLeft';
import ArrowRight from '@wix/da-ds/pkg/Icons/24x24/ChevronRight';
import FirstPageIcon from '@wix/da-ds/pkg/Icons/24x24/FirstPage';
import LastPageIcon from '@wix/da-ds/pkg/Icons/24x24/LastPage';
import { DEFAULT_THUMB_SIZE } from '../constants';

import s from './StudioPagination.scss';

export interface Props {
  className?: string;
  streamId: string;
  hasLess?: boolean;
  hasMore?: boolean;
  fromOffset: number;
  toOffset: number;
  resultsTotalCount?: number;
  fetchPrevious: (streamId: string) => void;
  fetchNext: (streamId: string) => void;
  fetchFirstPage?: (streamId: string) => void;
  fetchLastPage?: (streamId: string) => void;
  onFetchSuccess?: () => void;
  isSearchPage?: boolean;
  showButtons?: boolean;
  showResultsCount?: boolean;
  adaptCounterToThumbSize?: boolean;
  showFirstLastPageButtons?: boolean;
}

const StudioPagination: FC<Props> = ({
  className,
  streamId,
  hasLess,
  hasMore,
  fromOffset,
  toOffset,
  resultsTotalCount,
  fetchPrevious,
  fetchNext,
  fetchFirstPage,
  fetchLastPage,
  isSearchPage,
  showButtons: showButtonsProp = true,
  showResultsCount = true,
  adaptCounterToThumbSize = true,
  showFirstLastPageButtons = true,
}) => {
  const { t: translateFn } = useTranslation();
  const t = (key: string, options?: any): string =>
    translateFn(`studio.table.pagination.${key}`, options) as string;
  const isMobile = useContext(MobileContext);
  const showButtons = showButtonsProp;
  const isBlankSearch = isSearchPage && resultsTotalCount === 0;

  if (!showButtons && !showResultsCount) {
    return null;
  }

  const prevButton = showButtons && (
    <IconButton
      onClick={() => fetchPrevious(streamId)}
      disabled={!hasLess}
      biData={BiData<PageNavigationClickBiEvent>({
        evid: 113,
        direction: 'previous',
      })}
      tooltipText={t('previousPage.tooltip')}
      aria-label={t('previousPage.tooltip')}
    >
      <ArrowLeft size={IconSize.MEDIUM} />
    </IconButton>
  );

  const nextButton = showButtons && (
    <IconButton
      onClick={() => fetchNext(streamId)}
      disabled={!hasMore}
      biData={BiData<PageNavigationClickBiEvent>({
        evid: 113,
        direction: 'next',
      })}
      tooltipText={t('nextPage.tooltip')}
      aria-label={t('nextPage.tooltip')}
    >
      <ArrowRight size={IconSize.MEDIUM} />
    </IconButton>
  );

  const firstPageButton = showButtons && showFirstLastPageButtons && fetchFirstPage && (
    <IconButton
      onClick={() => fetchFirstPage?.(streamId)}
      disabled={!hasLess}
      biData={BiData<PageNavigationClickBiEvent>({
        evid: 113,
        direction: 'first',
      })}
      tooltipText={t('firstPage.tooltip')}
      aria-label={t('firstPage.tooltip')}
    >
      <FirstPageIcon size={IconSize.MEDIUM} />
    </IconButton>
  );

  const lastPageButton = showButtons && showFirstLastPageButtons && fetchLastPage && (
    <IconButton
      onClick={() => fetchLastPage?.(streamId)}
      disabled={!hasMore}
      biData={BiData<PageNavigationClickBiEvent>({
        evid: 113,
        direction: 'last',
      })}
      tooltipText={t('lastPage.tooltip')}
      aria-label={t('lastPage.tooltip')}
    >
      <LastPageIcon size={IconSize.MEDIUM} />
    </IconButton>
  );

  return (
    <div className={cn(s['root'], className)}>
      {isMobile && showButtons && (
        <div className={s['buttons']}>
          {firstPageButton}
          {prevButton}
        </div>
      )}

      {showResultsCount && (
        <div
          className={s['count']}
          style={{
            minWidth: adaptCounterToThumbSize ? DEFAULT_THUMB_SIZE : undefined,
          }}
        >
          {isBlankSearch && (
            <div className={s['no-results']}>{t('resultsCount.search.noResults')}</div>
          )}

          {!isBlankSearch &&
            (resultsTotalCount !== undefined ? (
              t(
                isSearchPage
                  ? hasMore || hasLess
                    ? 'resultsCount.search'
                    : 'resultsCount.search.singlePage'
                  : 'resultsCount',
                {
                  postProcess: 'reactPostprocessor',
                  offset: (
                    <span className={s['offset']}>
                      {fromOffset} - {toOffset}
                    </span>
                  ),
                  total: resultsTotalCount,
                }
              )
            ) : (
              <span className={s['offset']}>
                {fromOffset} - {toOffset}
              </span>
            ))}
        </div>
      )}

      {showButtons && (
        <div className={s['buttons']}>
          {!isMobile && firstPageButton}
          {!isMobile && prevButton}
          {nextButton}
          {lastPageButton}
        </div>
      )}
    </div>
  );
};
StudioPagination.displayName = 'StudioPagination';

export default StudioPagination;
